/**
 * @fileoverview Input/output schemas for the storyline-extraction-pipeline-v1 composite.
 *
 * Input accepts the text to analyze (synopsis or full text).
 * Output is a merged flat structure containing all A/B/C storylines,
 * compatible with the same transformFlatToStorylines utility used
 * by the individual extractors.
 */

import { z } from 'zod';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas/structure-phases';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  logId: z.string(),
  model: z.string(),
  projectId: z.string().optional(),
  synopsisInput: z.string().describe('The condensed synopsis or full text to analyze.'),
  isFullText: z.boolean().optional().describe(
    'When true, the synopsisInput contains the full story text rather than a condensed synopsis.'
  ),
  storyType: z.string().optional().describe('The story type (e.g., "Novelette", "Novel", "Epic"). Passed to sub-agents to calibrate beat density.'),
  projectedWordCount: z.number().optional().describe('The target word count for the story. Passed to sub-agents to calibrate beat density.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Merged output combining A/B/C storylines into a single flat structure.
 * Compatible with transformFlatToStorylines() for domain entity conversion.
 */
export const OutputSchema = z.object({
  storylines: z.array(z.object({
    name: z.string(),
    summary: z.string(),
    priority: z.enum(['A', 'B', 'C']),
  })),
  beats: z.array(z.object({
    storylineIndex: z.number(),
    globalPhase: StructurePhaseSchema,
    storylinePhase: StructurePhaseSchema,
    order: z.number().optional(),
    plotEvent: z.string(),
    narrativeState: z.string().optional(),
    goalProgress: z.number(),
  })),
  storyQuestions: z.array(z.object({
    storylineIndex: z.number(),
    question: z.string(),
    status: z.enum(['open', 'answered']).optional(),
  })),
});

export type Output = z.infer<typeof OutputSchema>;
